import { TEXT_INPUT_TYPES } from "../../../common/constants";
import { PMSValueFields } from "../models";

// General
export const INTEGRATION_TYPES = Object.freeze({
    LNS: "LNS",
    API: "API",
    PMS: "PMS",
} as const);

export const INTEGRATION_IDS = Object.freeze({
    BETTERSPACE_ACTILITY: "betterspace-actility-tpx",
    ACTILITY: "actility-tpx",
    TTI: "tti",
    OPENAPI: "api",
    IBELSA: "ibelsa",
    APALEO: "apaleo",
    GUESTLINE: "guestline",
    CLOCK: "clock",
    INFORHMS: "inforhms",
    PROTELIO: "protelio",
    ORACLESUITE8: "suite8",
    ORACLEOPERA: "opera",
} as const);

export const INTEGRATION_FEATURE_FLAGS = Object.freeze({
    ADD_DEVICE: "ADD_DEVICE",
    DELETE_DEVICE: "DELETE_DEVICE",
    SEND_PAYLOAD: "SEND_PAYLOAD",
    LNS_DEVICE_OVERVIEW: "LNS_DEVICE_OVERVIEW", // Link to LNS Device Overview
    OCCUPANCY_SYNC_MANUAL_BY_DATE_RANGE: "OCCUPANCY_SYNC_MANUAL_BY_DATE_RANGE", // Sync Now
    OCCUPANCY_SYNC_MANUAL_BY_BOOKING_ID: "OCCUPANCY_SYNC_MANUAL_BY_BOOKING_ID", // Sync Occupancy
    ROOM_SYNC_MANUAL: "ROOM_SYNC_MANUAL", // Sync Room Mapping
    ROOM_MAPPING_MANUAL: "ROOM_MAPPING_MANUAL", // Room Mapping Overview
} as const);

export const CONFIGURATION_SECRET = Object.freeze({
    primarySecret: "primarySecret",
    secondarySecret: "secondarySecret",
} as const);

export const MODAL_IDS = Object.freeze({
    // actility
    ACTILITY_CREATE_EDIT: "ACTILITY_CREATE_EDIT",

    // tti
    TTI_CREATE_EDIT: "TTI_CREATE_EDIT",

    // betterspace actility
    BETTERSPACE_ACTILITY_CREATE_EDIT: "BETTERSPACE_ACTILITY_CREATE_EDIT",

    // openapi
    OPENAPI_VIEW: "OPENAPI_VIEW",
    OPENAPI_ADD: "OPENAPI",
    OPENAPI_EDIT: "OPENAPI_EDIT",

    // pms
    PMS_CREATE_EDIT: "PMS_CREATE_EDIT",

    // common
    DELETE_INTEGRATION_CONFIG: "DELETE_INTEGRATION_CONFIG",
    PATCH_INTEGRATION_CONFIG_STATUS: "PATCH_INTEGRATION_CONFIG_STATUS",
    ROOM_MAPPING: "ROOM_MAPPING",
    SYNC_OCCUPANCIES: "SYNC_OCCUPANCIES",
    SYNC_OCCUPANCY: "SYNC_OCCUPANCY",
    SYNC_SPACES: "SYNC_SPACES",
} as const);

// Actility
export const ACTILITY_TYPES = {
    CLOUD_COMMUNITY: "CLOUD_COMMUNITY",
    CLOUD_ENTERPRISE_EU: "CLOUD_ENTERPRISE_EU",
    ON_PREMISE: "ON_PREMISE",
};

export const ACTILITY_URLS = {
    [ACTILITY_TYPES.CLOUD_COMMUNITY]: {
        DX_API_URL: "community.thingpark.io/",
        FLOW_API_URL: "community.thingpark.io/",
    },
    [ACTILITY_TYPES.CLOUD_ENTERPRISE_EU]: {
        DX_API_URL: "thingparkenterprise.eu.actility.com/",
        FLOW_API_URL: "thingparkenterprise.eu.actility.com/",
    },
    [ACTILITY_TYPES.ON_PREMISE]: {
        DX_API_URL: "",
        FLOW_API_URL: "",
    },
};

export const DEVELOPER_PORTAL_URL = "https://developer.betterspace.cloud";

export const SYNC_INTERVAL_OPTIONS = [15, 30, 45, 60];
export const TIME_RANGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

// PMS
export const INTEGRATION_VALUE_FIELDS_CONFIGURATIONS: Partial<
    Record<
        (typeof INTEGRATION_IDS)[keyof typeof INTEGRATION_IDS],
        PMSValueFields[]
    >
> = {
    [INTEGRATION_IDS.IBELSA]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "apiUrl",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "https://rooms.ibelsa.com/api",
        },
        {
            name: "apiKey",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.APALEO]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "propertyId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "BSAPALEO",
            initialValue: "",
        },
        {
            name: "username",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "password",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.INFORHMS]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "propertyId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "BSINFORHMS",
            initialValue: "",
        },
        {
            name: "username",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "password",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.GUESTLINE]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "propertyId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "BSGUESTLINE",
            initialValue: "",
        },
        {
            name: "username",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "password",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.CLOCK]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "subscriptionId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "12345",
            initialValue: "",
        },
        {
            name: "accountId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "54321",
            initialValue: "",
        },
        {
            name: "username",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "password",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.PROTELIO]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "token",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
            initialValue: "",
        },
        {
            name: "propertyId",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "1234",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.ORACLESUITE8]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "url",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder:
                "https://…/v8/fidelioiiswrapper.dll/FidelioXMLInterface.DataHandler",
            initialValue: "",
        },
        {
            name: "username",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "password",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
    ],
    [INTEGRATION_IDS.ORACLEOPERA]: [
        {
            name: "name",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "description",
            required: false,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "url",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder:
                "https://...oracleindustry.com/OPERAOSB/OPERA_OWS/OWS_WS_51",
            initialValue: "",
        },
        {
            name: "webServiceUsername",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "webServicePassword",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "interfaceUsername",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "interfacePassword",
            required: true,
            type: TEXT_INPUT_TYPES.PASSWORD,
            placeholder: "",
            initialValue: "",
        },
        {
            name: "hotelChainCode",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "CHAIN01",
            initialValue: "",
        },
        {
            name: "hotelCode",
            required: true,
            type: TEXT_INPUT_TYPES.TEXT,
            placeholder: "HOTEL01",
            initialValue: "",
        },
    ],
};
